import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  UploaderOptions,
  UploadFile,
  UploadInput,
  UploadOutput,
} from 'ngx-uploader-esb';
import { NgxUploaderModule } from 'ngx-uploader-esb';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';
import { SchedulePaymentComponent } from '../../../../pages/dashboard/components/schedule-payment/schedule-payment.component';
import { ThemesService } from '../../../../services/themes.service';
import { IDeal } from '../../../advance-application/models/deal.interface';
import {
  DealStatusEnum,
  DealStatusNames,
} from '../../../advance-application/models/deal-status.enum';
import { IFeeStructure } from '../../../advance-application/models/fee-structure.interface';
import { IPaymentAllocation } from '../../../advance-application/models/payment-allocation.interface';
import { IPromisedPayment } from '../../../advance-application/models/promised-payment.interface';
import { IPromisedPaymentList } from '../../../advance-application/models/promised-payment-list.interface';
import { PromisedPaymentStatusEnum } from '../../../advance-application/models/promised-payment-status.enum';
import { PromisedPaymentTypeEnum } from '../../../advance-application/models/promised-payment-type.enum';
import { IStatementData } from '../../../advance-application/models/statement-data.interface';
import { DealService } from '../../../advance-application/services/deal.service';
import { PromisedPaymentService } from '../../../advance-application/services/promised-payment.service';
import { V1IBankAccount } from '../../../authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { ConfirmModalService } from '../../../confirm-modal/confirm-modal.service';
import { CurrencyJsPipe } from '../../../pipes/currency-js.pipe';
import { DatePipe } from '../../../pipes/date.pipe';
import { LocalisePipe } from '../../../pipes/localise.pipe';
import { PreviewDocumentComponent } from '../../../shared/components/preview-document/preview-document.component';
import {
  BalanceByDeal,
  BalancesByDealsType,
} from '../../../shared/models/balance.interface';
import { DownloadEnum } from '../../../shared/models/download.enum';
import { FormatDateEnum } from '../../../shared/models/format-date.enum';
import { List } from '../../../shared/models/list.model';
import { TransactionExternalServiceNames } from '../../../shared/models/transaction-external-service.enum';
import { TransactionMethodNames } from '../../../shared/models/transaction-method.enum';
import { TransactionTypeNames } from '../../../shared/models/transaction-type.enum';
import { UserDealDocumentDocumentTypeEnum } from '../../../shared/models/user-deal-document-document-type.enum';
import { FileNamePipe } from '../../../shared/pipes/file-name.pipe';
import { FullAddressPipe } from '../../../shared/pipes/full-address.pipe';
import { GetDocumentByTypePipe } from '../../../shared/pipes/get-document-by-type.pipe';
import { IsStatementPipe } from '../../../shared/pipes/is-statement.pipe';
import { AccountService } from '../../../shared/services/account.service';
import { BankService } from '../../../shared/services/bank.service';
import { DateService } from '../../../shared/services/date.service';
import { DocumentService } from '../../../shared/services/document.service';
import { SharedService } from '../../../shared/services/shared.service';
import { TotalByPromisedListPipe } from '../deal-details-new/total-by-promised-list.pipe';
import { IsMoreDaysPipe } from './is-more-days.pipe';

export enum DealDetailsTabEnum {
  CommissionDetails = 0,
  Statement = 1,
  Agreement = 2,
}

@Component({
  selector: 'app-deal-details',
  templateUrl: './deal-details.component.html',
  styleUrls: ['./deal-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatDividerModule,
    MatButtonModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    NgxUploaderModule,
    LocalisePipe,
    CurrencyJsPipe,
    DatePipe,
    FullAddressPipe,
    GetDocumentByTypePipe,
    IsMoreDaysPipe,
    TotalByPromisedListPipe,
    IsStatementPipe,
    PreviewDocumentComponent,
    FileNamePipe,
  ],
})
export class DealDetailsComponent implements OnInit, OnDestroy {
  @Input() queryParams: any;

  public downloadEnum = DownloadEnum;
  public error = '';
  public paymentDateMin = this.dateService.addDaysToDate(new Date(), 1);
  public closingDateMin: Date;
  public promisedPaymentPreview?: IPromisedPayment[];
  public selectedIndex = DealDetailsTabEnum.CommissionDetails;
  public formatDateEnum = FormatDateEnum;
  public dealStatusNames: { [key: string]: string } = DealStatusNames;
  public dealStatusEnum = DealStatusEnum;
  public statusesForCancel = [
    DealStatusEnum.Approved,
    DealStatusEnum.GracePeriod,
    DealStatusEnum.Deferred,
    DealStatusEnum.Late,
  ];
  public userDealDocumentDocumentTypeEnum = UserDealDocumentDocumentTypeEnum;
  public loading = true;
  public loadingFile = false;
  public deal: IDeal;
  public dealExpectedClosingDate: Date; // closingDate + 5 days
  public promisedPayment?: IPromisedPayment;
  public requestedByUserPayment?: IPromisedPaymentList;
  public dealFeeStructures: IFeeStructure[] = [];
  public transactionList = new List<IPaymentAllocation>();
  public displayedColumnsTransaction: string[] = [
    'date',
    'amount',
    'unpaidAmount',
    'type',
    'method',
    'externalService',
    'creditCardNumber',
  ];
  public transactionExternalServiceNames: { [key: string]: string } =
    TransactionExternalServiceNames;
  public transactionMethodNames: { [key: string]: string } =
    TransactionMethodNames;
  public transactionTypeNames: { [key: string]: string } = TransactionTypeNames;
  public user = this.authenticationService.profile;
  public bankList: V1IBankAccount[] = [];
  public prevPromisedPayment?: IPromisedPaymentList;
  public promisedPaymentTypes = [
    { label: 'Manual', value: PromisedPaymentTypeEnum.Manual },
    { label: 'ACH', value: PromisedPaymentTypeEnum.ACH },
    { label: 'Payroll', value: PromisedPaymentTypeEnum.Payroll },
  ];
  public uploadInput = new EventEmitter<UploadInput>();
  public files: UploadFile[] = [];
  public dragOver: boolean;
  public balanceByDeals: BalancesByDealsType;
  public balance: BalanceByDeal;
  public options: UploaderOptions = { concurrency: 1, maxUploads: 1 };
  private subscriptions: Subscription[] = [];
  private dialogRef: MatDialogRef<any>;

  public maxDiffClosingDate = 15;
  public descriptionForClosingDate = '';
  public promisedPaymentFormBackup: any;
  public promisedPaymentForm = this.formBuilder.group({
    paymentDate: [undefined],
    closingDate: [undefined],
    bankAccountId: [undefined],
    type: [PromisedPaymentTypeEnum.Manual],
    field: [undefined], // paymentDate | closingDate
    userDealDocumentId: [undefined],
  });
  public statementData?: IStatementData;

  constructor(
    private dealService: DealService,
    private accountService: AccountService,
    public authenticationService: AuthenticationService,
    private dateService: DateService,
    private formBuilder: UntypedFormBuilder,
    private bankService: BankService,
    private promisedPaymentService: PromisedPaymentService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private documentService: DocumentService,
    private isMoreDaysPipe: IsMoreDaysPipe,
    private router: Router,
    private toastrService: ToastrService,
    private confirmModalService: ConfirmModalService,
    private isStatementPipe: IsStatementPipe,
    public sharedService: SharedService,
    public themesService: ThemesService,
  ) {}

  ngOnInit(): void {
    if (this.queryParams) {
      this.byQueryParams(this.queryParams);
    } else {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe((params) =>
          this.byQueryParams(params as any),
        ),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public byQueryParams(queryParams: { id: string; tab?: string }): void {
    if (queryParams.tab) {
      this.selectedIndex = parseInt(queryParams.tab, 10) as DealDetailsTabEnum;
    }

    this.load(parseInt(queryParams.id, 10));
  }

  public openMakeChanges(tmp: TemplateRef<HTMLElement>): void {
    delete this.promisedPaymentPreview;
    this.promisedPaymentForm.reset();

    if (this.promisedPayment?.bankAccountId) {
      this.promisedPaymentForm
        .get('bankAccountId')
        ?.setValue(this.promisedPayment.bankAccountId);
    }
    if (this.promisedPayment?.type) {
      this.promisedPaymentForm.get('type')?.setValue(this.promisedPayment.type);
    }
    if (this.deal.closingDate) {
      this.promisedPaymentForm
        .get('closingDate')
        ?.setValue(
          this.dateService.parseAnyDateFormatToDate(this.deal.closingDate),
        );
      // this.updatedClosingDate(true);

      if (!this.promisedPayment) {
        this.closingDateMin = this.dateService.addDaysToDate(
          this.dateService.yyyymmddToDate(this.deal.closingDate),
          1,
        );
      }
    }
    if (this.deal?.paymentDate) {
      this.promisedPaymentForm
        .get('paymentDate')
        ?.setValue(
          this.dateService.parseAnyDateFormatToDate(this.deal.paymentDate),
        );
    }
    if (this.promisedPayment?.calculationDate) {
      this.promisedPaymentForm
        .get('paymentDate')
        ?.setValue(
          this.dateService.parseAnyDateFormatToDate(
            this.promisedPayment.calculationDate,
          ),
        );
    }
    this.promisedPaymentForm.get('userDealDocumentId')?.setValue(undefined);

    this.promisedPaymentFormBackup = this.promisedPaymentForm.getRawValue();
    if (this.promisedPayment?.calculationDate) {
      this.dialogRef = this.dialog.open(tmp, {
        width: '730px',
        panelClass: this.themesService.getDarkThemeClass(),
        backdropClass: this.themesService.getDarkThemeClass(),
      });
    } else {
      this.dialogRef = this.dialog.open(tmp, {
        width: '375px',
        panelClass: this.themesService.getDarkThemeClass(),
        backdropClass: this.themesService.getDarkThemeClass(),
      });
    }
    this.dialogRef
      .afterClosed()
      .subscribe(() => delete this.promisedPaymentPreview);
  }

  private async load(dealId: number): Promise<void> {
    this.loading = true;
    this.deal = await firstValueFrom(this.dealService.get(dealId));
    this.loading = false;
    this.balanceByDeals = await firstValueFrom(
      this.accountService.getBalanceByDeals(),
    );
    this.balance = this.balanceByDeals[dealId];
    firstValueFrom(this.dealService.getPaidTransactionsList(dealId)).then(
      (data) => (this.transactionList = data),
    );

    this.promisedPaymentService
      .getList({
        limit: 1,
        offset: 0,
        status: [
          PromisedPaymentStatusEnum.Scheduled,
          PromisedPaymentStatusEnum.Processing,
        ],
        sortDirection: 'asc',
        sortBy: 'effectiveDate',
        effectiveDateTo: this.dateService.addDaysToDate(new Date(), -1),
      })
      .toPromise()
      .then((res) => (this.prevPromisedPayment = res?.list && res.list[0]));

    this.promisedPaymentService
      .getList({
        dealId,
        limit: 1,
        offset: 0,
        status: PromisedPaymentStatusEnum.Scheduled,
      })
      .toPromise()
      .then(
        (data) => {
          this.requestedByUserPayment = data?.list[0];
        },
        (e) => {
          delete this.requestedByUserPayment;
          this.error = this.sharedService.getErrorText(e, true);
        },
      );

    const promisedPaymentPromise = this.promisedPaymentService
      .getByDealId(dealId)
      .toPromise();
    promisedPaymentPromise.then(async (data) => {
      if (data?.list?.length) {
        this.promisedPayment = data.list[0];
        this.loadBankAccounts();

        if (this.isStatementPipe.transform(this.deal)) {
          this.statementData = await this.promisedPaymentService
            .getStatementData(this.promisedPayment.id)
            .toPromise();
        }
      } else {
        delete this.promisedPayment;
      }
    });
    promisedPaymentPromise.catch(() => delete this.promisedPayment);

    this.dealExpectedClosingDate = this.dateService.addDaysToDate(
      this.dateService.parseAnyDateFormatToDate(this.deal.closingDate),
      5,
    );
    if (
      [DealStatusEnum.Approved, DealStatusEnum.GracePeriod].includes(
        this.deal.status,
      )
    ) {
      await this.loadDealFeeStructures(dealId);
    } else {
      this.dealFeeStructures = [];
    }
  }

  public async removeDocument(id: number): Promise<void> {
    this.loadingFile = true;

    try {
      await this.documentService.deleteDocument(id).toPromise();
      this.load(this.deal.id);
    } catch (e) {
      this.error = this.sharedService.getErrorText(e);
    }

    this.loadingFile = false;
  }

  public downloadStatement(promisedPaymentId: number): void {
    window.open(
      `/api/promised-payment/${promisedPaymentId}/statement?download=${DownloadEnum.Attachment}`,
      '_blank',
    );
  }

  private async loadDealFeeStructures(dealId: number): Promise<void> {
    this.dealFeeStructures = this.dealService.calcFeeStructures(
      this.dateService.parseAnyDateFormatToDate(this.deal.closingDate),
      this.dateService.parseAnyDateFormatToDate(this.deal.firstApprovedDate),
      this.deal.pricingPolicy?.pricingPolicyItems || [],
    );
  }

  async loadBankAccounts(): Promise<void> {
    this.bankList = await firstValueFrom(this.bankService.getList());
  }

  public updatedClosingDate(skipUpdate?: boolean): void {
    const closingDate = this.promisedPaymentForm.get('closingDate')?.value;
    if (!closingDate) {
      return;
    }

    // this.paymentDateMin = closingDate;

    if (!skipUpdate) {
      this.promisedPaymentForm
        .get('paymentDate')
        ?.setValue(
          this.dateService.addDaysToDate(
            this.dateService.parseAnyDateFormatToDate(closingDate),
            5,
          ),
        );
      this.promisedPaymentForm.get('field')?.setValue('closingDate');

      if (this.promisedPayment) {
        this.updatedPromisedPayment(true);
      }
    }
  }

  public updatedPaymentDate(skipUpdate?: boolean): void {
    this.promisedPaymentForm.get('field')?.setValue('paymentDate');

    if (!skipUpdate) {
      const closingDate = this.dateService.parseAnyDateFormatToDate(
        this.deal.closingDate,
      );
      // this.paymentDateMin = closingDate;
      this.promisedPaymentForm.get('closingDate')?.setValue(closingDate);

      if (this.promisedPayment) {
        this.updatedPromisedPayment(true);
      }
    }
  }

  public async updatedPromisedPayment(dry = false): Promise<void> {
    this.error = '';
    let raw = this.promisedPaymentForm.getRawValue();
    if (raw.field) {
      delete raw[raw.field === 'closingDate' ? 'paymentDate' : 'closingDate'];
    }

    raw = this.sharedService.getDiff(raw, this.promisedPaymentFormBackup);
    if (raw.closingDate) {
      raw.closingDate = this.dateService.dateToYyyymmdd(raw.closingDate);
    }
    if (raw.calculationDate) {
      raw.calculationDate = this.dateService.dateToYyyymmdd(
        raw.calculationDate,
      );
    }

    try {
      this.promisedPaymentPreview = await this.promisedPaymentService
        .update(this.promisedPayment?.id || 0, {
          ...raw,
          dryRun: dry,
        })
        .toPromise();
    } catch (e) {
      this.error = this.sharedService.getErrorText(e, true);
    }

    if (!dry) {
      this.load(this.deal.id);
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    }
  }

  public async updateDealPaymentDate(): Promise<void> {
    this.error = '';
    const raw = this.promisedPaymentForm.getRawValue();

    try {
      await this.dealService
        .updatePaymentDate(this.deal.id, {
          paymentDate: raw.paymentDate,
        })
        .toPromise();

      this.load(this.deal.id);
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    } catch (e) {
      this.error = this.sharedService.getErrorText(e);
    }
  }

  public async updateDealClosingDate(): Promise<void> {
    this.error = '';
    const raw = this.promisedPaymentForm.getRawValue();

    if (
      this.isMoreDaysPipe.transform(
        raw.closingDate,
        this.deal.closingDate,
        this.maxDiffClosingDate,
      ) &&
      !raw.userDealDocumentId
    ) {
      this.error = `In order to update the closing date by more than ${this.maxDiffClosingDate} days, you need to upload the extension / addendum document`;
      return;
    }

    try {
      await this.dealService
        .updateClosingDate(this.deal.id, {
          closingDate: this.dateService.dateToYyyymmdd(raw.closingDate),
          userDealDocumentId: raw.userDealDocumentId,
        })
        .toPromise();

      this.load(this.deal.id);
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    } catch (e) {
      this.error = this.sharedService.getErrorText(e);
    }
  }

  /**
   * Handle uploads.
   */
  public onUploadOutput(
    output: UploadOutput,
    type = UserDealDocumentDocumentTypeEnum.ContractOfSale,
    description = '',
  ): void {
    switch (output.type) {
      case 'allAddedToQueue':
        if (!this.files?.length) {
          return;
        }

        const event: UploadInput = {
          type: 'uploadAll',
          url:
            environment.apiUrl +
            `/api/deal/${
              this.deal.id
            }/document?documentType=${type}&description=${encodeURIComponent(
              description,
            )}`,
          method: 'POST',
          includeWebKitFormBoundary: true,
        };
        this.loadingFile = true;
        this.uploadInput.emit(event);
        break;
      case 'addedToQueue':
        if (typeof output.file !== 'undefined') {
          this.files.push(output.file);
        }
        break;
      case 'uploading':
        if (typeof output.file !== 'undefined') {
          // update current data in files array for uploading file
          const index = this.files.findIndex(
            (file) =>
              typeof output.file !== 'undefined' && file.id === output.file.id,
          );
          this.files[index] = output.file;
        }
        break;
      case 'removed':
        // remove file from array when removed
        this.files = this.files.filter(
          (file: UploadFile) => file !== output.file,
        );
        break;
      case 'dragOver':
        this.dragOver = true;
        break;
      case 'dragOut':
      case 'drop':
        this.dragOver = false;
        break;
      case 'done':
        if (
          output.file &&
          [200, 201].includes(output.file?.responseStatus as number)
        ) {
          if (type === UserDealDocumentDocumentTypeEnum.ExpansionAddendum) {
            this.promisedPaymentForm.controls.userDealDocumentId.setValue(
              parseInt(output.file.response.id, 10),
            );
          } else {
            this.load(this.deal.id);
          }

          this.error = '';
        } else {
          this.error = output.file?.response.error;
        }

        this.loadingFile = false;
        this.removeFile(output.file?.id || '');
        break;
    }
  }

  public cancelUpload(id: string): void {
    this.uploadInput.emit({ type: 'cancel', id });
  }

  public removeFile(id: string): void {
    this.uploadInput.emit({ type: 'remove', id });
  }

  schedulePayment(dealId: number): void {
    const dialogRef = this.dialog.open(SchedulePaymentComponent, {
      panelClass: this.themesService.getDarkThemeClass(),
      backdropClass: this.themesService.getDarkThemeClass(),
    });
    dialogRef.componentInstance.dealId = dealId;
    dialogRef
      .afterClosed()
      .pipe(filter((res) => res))
      .subscribe(() => {
        this.load(dealId);
      });
  }

  // public cancelDeal(deal: IDeal): void {
  //   this.confirmModalService.openConfirm({
  //     confirmAction: async () => {
  //       this.loading = true;
  //
  //       try {
  //         await this.dealService.cancelDeal(deal.id).toPromise();
  //         this.toastrService.success('Success!');
  //         this.router.navigate(['/dashboard']);
  //       } catch (e) {
  //         this.toastrService.error(this.sharedService.getErrorText(e, true));
  //       }
  //
  //       this.loading = false;
  //     },
  //     title: 'Are you sure you want to cancel a deal?',
  //     confirmText: 'Yes',
  //   });
  // }
}
